<template>
  <div class="checkin-page" :class="isDark ? 'theme-dark' : 'theme-light'" :style="rootCssVars">

    <!-- Theme Toggle Button -->
<button class="theme-toggle" @click="isDark = !isDark" :title="isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'">
  <v-icon v-if="isDark" size="22" color="#f1f5f9">mdi-white-balance-sunny</v-icon>
  <v-icon v-else size="22" color="#1e293b">mdi-moon-waning-crescent</v-icon>
</button>

    <!-- Loading Screen -->
    <div v-if="pageLoading" class="loading-screen">
      <div class="loading-spinner"></div>
      <p class="loading-text">Loading society details...</p>
    </div>

    <!-- Error Screen -->
    <div v-else-if="pageError" class="error-screen">
      <div class="error-icon">⚠️</div>
      <h2>Society Not Found</h2>
      <p>{{ pageError }}</p>
    </div>

    <!-- Success Screen -->
    <div v-else-if="submitted" class="success-screen">
      <div class="success-icon">
        <svg viewBox="0 0 52 52" class="checkmark">
          <circle class="checkmark-circle" cx="26" cy="26" r="25" fill="none" />
          <path class="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
        </svg>
      </div>
      <h2 class="success-title">Request Submitted!</h2>
      <p class="success-msg">Your check-in request has been sent. The security guard will verify your details and notify the resident.</p>
      <div class="success-steps">
        <div class="step">
          <span class="step-num" :style="{ background: themeBtnColor }">1</span>
          <span>Guard verifies your entry</span>
        </div>
        <div class="step">
          <span class="step-num" :style="{ background: themeBtnColor }">2</span>
          <span>Resident approves or rejects</span>
        </div>
        <div class="step">
          <span class="step-num" :style="{ background: themeBtnColor }">3</span>
          <span>Guard allows your entry</span>
        </div>
      </div>
      
      <div class="success-actions" style="margin-top: 32px; text-align: center;">
        <button class="btn-primary" :style="btnPrimaryStyle" @click="resetForm" style="width: 100%; max-width: 300px;">
          Add Another Entry
        </button>
      </div>
    </div>

    <!-- Main Form -->
    <div v-else class="checkin-container">

      <!-- Header -->
      <div class="checkin-header" :style="headerStyle">
       <div class="society-logo-wrap">
  <img
    v-if="society && society.logo_url && !logoLoadError"
    :src="apiBase + '/uploads/society/logos/' + society.logo_url"
    class="society-logo"
    alt="Society Logo"
    @error="logoLoadError = true"
  />
  <div v-else class="society-logo-placeholder">{{ societyInitials }}</div>
</div>
        <div class="society-info">
          <h1 class="society-name">{{ society ? society.name : 'iSmart Society' }}</h1>
          <p class="checkin-label">Visitor Self Check-in</p>
        </div>
      </div>

      <!-- Progress Bar -->
      <div class="progress-wrap">
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: progressPercent + '%', ...progressFillStyle }"></div>
        </div>
        <span class="progress-text">Step {{ step }} of 3</span>
      </div>

      <!-- ───────── Step 1: Select House ───────── -->
      <transition name="slide" mode="out-in">
        <div v-if="step === 1" key="step1" class="form-step">

          <div class="step-header">
            <div class="step-icon" :style="stepIconStyle">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
            </div>
            <h2 class="step-title">Select Unit</h2>
            <p class="step-subtitle">Choose the block and house number you are visiting</p>
          </div>

          <!-- Block dropdown — only for multi-building societies -->
          <div class="form-group" v-if="useBlocks">
            <label class="form-label">Block</label>
            <div class="unit-search-wrap" id="flat-wrap">
              <div class="unit-search-input-wrap" @click="flatDropdownOpen = true">
                <span v-if="form.flat_id" class="unit-selected-num">{{ selectedFlatName }}</span>
                <span v-else class="unit-placeholder">Select Block</span>
                <span class="select-icon">▾</span>
              </div>
              <div v-if="flatDropdownOpen" class="unit-dropdown" id="flat-dropdown">
                <div class="unit-list">
                  <div class="unit-option" :class="{ selected: !form.flat_id }" @click="selectFlat('')">
                    <span class="unit-option-num">Select Block</span>
                  </div>
                  <div
                    v-for="flat in flats"
                    :key="flat.id"
                    class="unit-option"
                    :class="{ selected: form.flat_id === flat.id }"
                    @click="selectFlat(flat.id)"
                  >
                    <span class="unit-option-num">{{ flat.name }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- House / Unit dropdown -->
          <div class="form-group">
            <label class="form-label">{{ unitLabel }} <span class="required">*</span></label>
            <div v-if="housesLoading" class="loading-inline">Loading houses...</div>
            <div v-else class="unit-search-wrap" id="house-wrap">
              <div class="unit-search-input-wrap" @click="unitDropdownOpen = true">
                <template v-if="selectedUnit">
                  <span class="unit-selected-num">{{ housePrefix + selectedUnit.house_number }}</span>
                  <!-- <span v-if="selectedUnit.name" class="unit-selected-name">({{ selectedUnit.name }})</span> -->
                </template>
                <span v-else class="unit-placeholder">Select House</span>
                <span class="select-icon">▾</span>
              </div>
              <div v-if="unitDropdownOpen" class="unit-dropdown">
                <div class="unit-search-box">
                  <input
                    ref="unitSearch"
                    v-model="unitSearchQuery"
                    type="text"
                    class="unit-search-field"
                    placeholder="Search by house no"
                    @click.stop
                  />
                </div>
                <div class="unit-list">
                  <div
                    v-for="house in filteredHouses"
                    :key="house.id"
                    class="unit-option"
                    :class="{ selected: selectedUnit && selectedUnit.id === house.id }"
                    @click="selectUnit(house)"
                  >
                    <span class="unit-option-num">{{ housePrefix + house.house_number }}</span>
                    <!-- <span v-if="house.name" class="unit-option-name">({{ house.name }})</span> -->
                  </div>
                  <div v-if="filteredHouses.length === 0" class="unit-no-result">No results found</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Resident preview card -->
          <!-- <div v-if="selectedUnit && selectedUnit.name" class="resident-card">
            <div class="resident-avatar">{{ selectedUnit.name.charAt(0).toUpperCase() }}</div>
            <div class="resident-info">
              <span class="resident-label">Visiting</span>
              <span class="resident-name">{{ selectedUnit.name }}</span>
            </div>
            <div class="resident-check">✓</div>
          </div> -->

          <div v-if="step1Error" class="form-error">{{ step1Error }}</div>

          <button class="btn-primary" :style="btnPrimaryStyle" @click="goStep2">
            Continue <span class="btn-arrow">→</span>
          </button>
        </div>
      </transition>

      <!-- ───────── Step 2: Visitor Details ───────── -->
      <transition name="slide" mode="out-in">
        <div v-if="step === 2" key="step2" class="form-step">

          <div class="step-header">
            <div class="step-icon" :style="stepIconStyle">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </div>
            <h2 class="step-title">Your Details</h2>
            <p class="step-subtitle">Please enter your contact information</p>
          </div>

          <div class="form-group">
            <label class="form-label">Full Name <span class="required">*</span></label>
            <input v-model="form.name" type="text" class="form-input" placeholder="Enter your full name" />
          </div>

          <div class="form-group">
            <label class="form-label">Mobile Number <span class="required">*</span></label>
            <div class="phone-wrap">
                <vue-country-code @onSelect="onSelect" class="custom-country-code-select" defaultCountry="IN" />

              <input v-model="form.mobile_no" type="tel" class="phone-input" placeholder="Enter mobile number"  />
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">Purpose of Visit</label>
            <div class="purpose-chips">
              <button
                v-for="p in purposes"
                :key="p"
                class="purpose-chip"
                :class="{ active: form.purpose === p }"
                @click="form.purpose = p"
                type="button"
              >{{ p }}</button>
            </div>
            <input v-model="form.purpose" type="text" class="form-input mt-8" placeholder="Or type your purpose..." />
          </div>

          <div v-if="step2Error" class="form-error">{{ step2Error }}</div>

          <div class="btn-row">
            <button class="btn-secondary" @click="step = 1">← Back</button>
            <button class="btn-primary" :style="btnPrimaryStyle" @click="goStep3">Continue →</button>
          </div>
        </div>
      </transition>

      <!-- ───────── Step 3: Additional Details ───────── -->
      <transition name="slide" mode="out-in">
        <div v-if="step === 3" key="step3" class="form-step">

          <div class="step-header">
            <div class="step-icon" :style="stepIconStyle">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="4" width="18" height="16" rx="2" ry="2"></rect>
                <circle cx="9" cy="10" r="2"></circle>
                <path d="M15 16v-1a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v1"></path>
                <line x1="16" y1="10" x2="19" y2="10"></line>
                <line x1="16" y1="14" x2="19" y2="14"></line>
              </svg>
            </div>
            <h2 class="step-title">Additional Details</h2>
            <p class="step-subtitle">Add vehicle info and your photo</p>
          </div>

          <div class="photo-upload-row" style="display: flex; gap: 12px; margin-bottom: 20px;">
            <div class="form-group" style="flex: 1; margin-bottom: 0;">
              <label class="form-label">Vehicle Photo <span class="optional">(Optional)</span></label>
              <div class="photo-upload-box" @click="$refs.vehiclePhotoInput.click()">
                <img v-if="vehiclePhotoPreview" :src="vehiclePhotoPreview" class="photo-preview" alt="Preview" />
                <div v-else class="photo-placeholder">
                  <span class="photo-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <circle cx="8.5" cy="8.5" r="1.5"></circle>
                      <polyline points="21 15 16 10 5 21"></polyline>
                    </svg>
                  </span>
                  <span>Tap to add vehicle photo</span>
                </div>
              </div>
              <input ref="vehiclePhotoInput" type="file" accept="image/*" capture="environment" @change="onVehiclePhotoChange" style="display:none" />
            </div>

            <div class="form-group" style="flex: 1; margin-bottom: 0;">
              <label class="form-label">Visitor Photo</label>
              <div class="photo-upload-box" @click="$refs.photoInput.click()">
                <img v-if="photoPreview" :src="photoPreview" class="photo-preview" alt="Preview" />
                <div v-else class="photo-placeholder">
                  <span class="photo-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                      <circle cx="12" cy="13" r="4"></circle>
                    </svg>
                  </span>
                  <span>Tap to add photo</span>
                </div>
              </div>
              <input ref="photoInput" type="file" accept="image/*" capture="user" @change="onPhotoChange" style="display:none" />
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">Vehicle Number <span class="optional">(Optional)</span></label>
            <input v-model="form.vehicle_no" type="text" class="form-input" placeholder="e.g. GJ-01-AB-1234" style="text-transform:uppercase" />
          </div>

          <!-- Summary Card -->
          <div class="summary-card">
            <h3 class="summary-title">Summary</h3>
            <!-- <div class="summary-row">
              <span class="summary-key">Visiting</span>
              <span class="summary-val">{{ selectedUnit && selectedUnit.name ? selectedUnit.name : '—' }}</span>
            </div> -->
            <div class="summary-row">
              <span class="summary-key">Unit</span>
              <span class="summary-val">{{ selectedUnit ? (housePrefix + selectedUnit.house_number) : '—' }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-key">Visitor</span>
              <span class="summary-val">{{ form.name }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-key">Mobile</span>
              <span class="summary-val">{{ form.country_code }} {{ form.mobile_no }}</span>
            </div>
            <div class="summary-row" v-if="form.purpose">
              <span class="summary-key">Purpose</span>
              <span class="summary-val">{{ form.purpose }}</span>
            </div>
          </div>

          <div v-if="submitError" class="form-error">{{ submitError }}</div>

          <div class="btn-row">
            <button class="btn-secondary" @click="step = 2">← Back</button>
            <button
              class="btn-submit"
              :style="submitBtnStyle"
              @click="submitCheckin"
              :disabled="submitting"
            >
              <span v-if="submitting" class="spinner-sm"></span>
              <span v-else>✓ Submit Check-in</span>
            </button>
          </div>
        </div>
      </transition>

    </div>
  </div>
</template>

<script>
import axios from 'axios';

const API_BASE = process.env.VUE_APP_API_URL;

export default {
  name: 'VisitorCheckin',

  data() {
    return {
      /* ── theme ── */
      isDark: false,           // false = light mode (default), true = dark mode

      /* ── page state ── */
      pageLoading: true,
      pageError: null,
      society: null,

      /* ── form state ── */
      step: 1,
      submitted: false,
      submitting: false,
      submitError: null,
      step1Error: null,
      step2Error: null,

      /* ── data ── */
      flats: [],
      houses: [],
      housesLoading: false,
      selectedUnit: null,
      unitDropdownOpen: false,
      flatDropdownOpen: false,
      unitSearchQuery: '',
      photoPreview: null,
      photoFile: null,
      vehiclePhotoPreview: null,
      vehiclePhotoFile: null,

      form: {
        flat_id: '',
        house_number: '',
        member_id: '',
        name: '',
        mobile_no: '',
        country_code: '+91',
        purpose: '',
        vehicle_no: '',
        unit_mapping_id: '',
      },

      purposes: ['Family Visit', 'Delivery', 'Maintenance', 'Friend/Guest', 'Medical', 'Business'],
      apiBase: API_BASE,
      logoLoadError: false,
    };
  },

  computed: {
    rootCssVars() {
      return {
        '--theme-color': this.themeColor,
        '--theme-color-dark': this.themeColorDark,
        '--theme-color-soft': this.themeColor + '18',   // light tint for bg
        '--theme-color-ring': this.themeColor + '1a',   // focus ring opacity
      };
    },
    societyId() {
      return this.$route.params.id;
    },
    societyInitials() {
      const name = this.society ? this.society.name : 'iSmart Society';
      if (!name) return 'IS';
      const words = name.trim().split(/\s+/);
      return words.length >= 2
        ? (words[0][0] + words[1][0]).toUpperCase()
        : name.substring(0, 2).toUpperCase();
    },
    progressPercent() {
      return (this.step / 3) * 100;
    },
    selectedFlatName() {
      if (!this.form.flat_id || this.form.flat_id === 'single_house' || this.form.flat_id === '0') return '';
      if (this.form.flat_id === 'all_shops') return 'All Shops';
      const flat = this.flats.find(f => f.id === this.form.flat_id);
      return flat ? flat.name : '';
    },
    housePrefix() {
      if (this.form.flat_id === 'all_shops') return 'Shop-';
      if (this.selectedFlatName) return this.selectedFlatName + '-';
      return '';
    },
    useBlocks() {
      return this.society && this.society.has_multiple_building == 1;
    },
    societyType() {
      return this.society ? Number(this.society.society_type) : 1;
    },
    unitLabel() {
      if (this.societyType === 2) return 'Shop No';
      if (this.societyType === 3) return 'House No / Shop No';
      return 'House No';
    },
    filteredHouses() {
      const q = this.unitSearchQuery.trim().toLowerCase();
      if (!q) return this.houses;
      return this.houses.filter(h =>
        (h.house_number && h.house_number.toLowerCase().includes(q)) ||
        (h.name && h.name.toLowerCase().includes(q))
      );
    },

    /* ── theme colours ── */
    themeColor() {
      return (this.society && this.society.theme_primary_color)
        ? this.society.theme_primary_color : '#6366f1';
    },
    themeBtnColor() {
      return (this.society && this.society.theme_button_color)
        ? this.society.theme_button_color : this.themeColor;
    },
    themeColorDark() {
      return this.shadeColor(this.themeColor, -20);
    },

    /* ── inline styles ── */
    headerStyle() {
      // In dark mode, ignore the society's theme color and use a fixed dark gradient.
      if (this.isDark) {
        return {
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
        };
      }
      return {
        background: `linear-gradient(135deg, ${this.themeColor} 0%, ${this.themeColorDark} 100%)`,
      };
    },
    btnPrimaryStyle() {
      // In dark mode, ignore the society's theme color and use a fixed dark gradient.
      if (this.isDark) {
        return {
          background: 'linear-gradient(135deg, #334155 0%, #1e293b 100%)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
        };
      }
      return {
        background: `linear-gradient(135deg, ${this.themeBtnColor} 0%, ${this.shadeColor(this.themeBtnColor, -15)} 100%)`,
        boxShadow: `0 4px 20px ${this.themeBtnColor}55`,
      };
    },
    submitBtnStyle() {
      // In dark mode, ignore the society's theme color and use a fixed dark gradient.
      if (this.isDark) {
        return {
          background: 'linear-gradient(135deg, #334155 0%, #1e293b 100%)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
        };
      }
      return {
        background: `linear-gradient(135deg, ${this.themeBtnColor}, ${this.shadeColor(this.themeBtnColor, -20)})`,
        boxShadow: `0 4px 20px ${this.themeBtnColor}55`,
      };
    },
    progressFillStyle() {
      return { background: `linear-gradient(90deg, ${this.themeColor}, ${this.themeColorDark})` };
    },
    stepIconStyle() {
      return { background: `${this.themeColor}18`, color: this.themeColor };
    },
  },

  watch: {
    society(newVal) {
      if (newVal) this.logoLoadError = false;
    },
    selectedUnit(val) {
      if (val) {
        this.form.house_number = val.house_number;
        this.form.member_id = val.id;
        this.form.unit_mapping_id = val.unit_mapping_id || '';
      } else {
        this.form.house_number = '';
        this.form.member_id = '';
        this.form.unit_mapping_id = '';
      }
    },
    unitDropdownOpen(val) {
      if (val) {
        this.$nextTick(() => { if (this.$refs.unitSearch) this.$refs.unitSearch.focus(); });
        document.addEventListener('click', this.closeUnitDropdown);
      } else {
        document.removeEventListener('click', this.closeUnitDropdown);
      }
    },
    flatDropdownOpen(val) {
      if (val) document.addEventListener('click', this.closeFlatDropdown);
      else document.removeEventListener('click', this.closeFlatDropdown);
    },
  },

  methods: {
    resetForm() {
      this.submitted = false;
      this.step = 1;
      this.form = {
        flat_id: '',
        house_number: '',
        member_id: '',
        name: '',
        mobile_no: '',
        country_code: '+91',
        purpose: '',
        vehicle_no: '',
        unit_mapping_id: '',
      };
      this.selectedUnit = null;
      this.photoFile = null;
      this.photoPreview = null;
      this.vehiclePhotoFile = null;
      this.vehiclePhotoPreview = null;
      this.submitError = null;
      this.step1Error = null;
      this.step2Error = null;
    },

    onSelect(event) {
      this.form.country_code = "+" + event.dialCode;
    },

    shadeColor(hex, amount) {
      if (!hex || !hex.startsWith('#')) return hex || '#6366f1';
      let c = hex.replace('#', '');
      if (c.length === 3) c = c.split('').map(x => x + x).join('');
      const num = parseInt(c, 16);
      const r = Math.min(255, Math.max(0, (num >> 16) + amount));
      const g = Math.min(255, Math.max(0, ((num >> 8) & 0xff) + amount));
      const b = Math.min(255, Math.max(0, (num & 0xff) + amount));
      return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
    },

    async loadSociety() {
      try {
        const res = await axios.get(`${API_BASE}/society/${this.societyId}/details`);
        if (res.data.status) {
          this.society = res.data.society;
        } else {
          this.pageError = res.data.message || 'Society not found';
        }
      } catch {
        this.pageError = 'Unable to load society information. Please try again.';
      } finally {
        this.pageLoading = false;
      }
    },

    async loadFlats() {
      try {
        const res = await axios.get(`${API_BASE}/society/${this.societyId}/flats`);
        if (res.data && res.data.flats) {
          let flats = res.data.flats;
          if ((this.societyType === 2 || this.societyType === 3) && this.useBlocks) {
            flats = [{ id: 'all_shops', name: 'All Shops' }, ...flats];
          }
          this.flats = flats;
          if (!this.useBlocks) this.loadHouses(null);
        }
      } catch {
        this.loadHouses(null);
      }
    },

    async loadHouses(flatId) {
      this.housesLoading = true;
      this.houses = [];
      this.selectedUnit = null;
      this.unitSearchQuery = '';
      this.unitDropdownOpen = false;
      try {
        const params = {};
        if (flatId && flatId !== 'all_shops') params.flat_id = flatId;
        if (flatId === 'all_shops') params.unit_type = 2;
        const res = await axios.get(`${API_BASE}/society/${this.societyId}/houses`, { params });
        if (res.data && res.data.status) {
          const raw = res.data.houses;
          this.houses = Array.isArray(raw) ? raw : (raw.toJSON ? raw.toJSON() : Object.values(raw));
        }
      } catch { /* ignore */ } finally {
        this.housesLoading = false;
      }
    },

    onFlatChange() {
      this.selectedUnit = null;
      this.unitSearchQuery = '';
      this.unitDropdownOpen = false;
      this.loadHouses(this.form.flat_id || null);
    },

    selectFlat(id) {
      this.form.flat_id = id;
      this.flatDropdownOpen = false;
      this.onFlatChange();
    },

    closeFlatDropdown(e) {
      if (!this.$el.querySelector('#flat-wrap')?.contains(e.target)) this.flatDropdownOpen = false;
    },

    selectUnit(house) {
      this.selectedUnit = house;
      this.unitDropdownOpen = false;
      this.unitSearchQuery = '';
    },

    closeUnitDropdown(e) {
      if (!this.$el.querySelector('#house-wrap')?.contains(e.target)) this.unitDropdownOpen = false;
    },

    goStep2() {
      this.step1Error = null;
      if (!this.selectedUnit) { this.step1Error = 'Please select a house / unit to continue.'; return; }
      this.step = 2;
    },

    goStep3() {
      this.step2Error = null;
      if (!this.form.name?.trim()) { this.step2Error = 'Please enter your full name.'; return; }
      if (!this.form.mobile_no?.trim()) { this.step2Error = 'Please enter your mobile number.'; return; }
      if (this.form.mobile_no.trim().length !== 10) { this.step2Error = 'Mobile number must be exactly 10 digits.'; return; }
      this.step = 3;
    },

    onPhotoChange(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.photoFile = file;
      const reader = new FileReader();
      reader.onload = ev => { this.photoPreview = ev.target.result; };
      reader.readAsDataURL(file);
    },

    // Compress + auto-rotate image before sending to Python API
    compressAndRotate(file) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            // Max 1000px wide – enough for plate OCR, keeps file small
            const MAX = 1000;
            let w = img.width, h = img.height;
            if (w > MAX) { h = Math.round(h * MAX / w); w = MAX; }
            const canvas = document.createElement('canvas');
            canvas.width = w; canvas.height = h;
            canvas.getContext('2d').drawImage(img, 0, 0, w, h);
            canvas.toBlob((blob) => {
              resolve(new File([blob], 'plate.jpg', { type: 'image/jpeg' }));
            }, 'image/jpeg', 0.92);
          };
          img.src = e.target.result;
        };
        reader.readAsDataURL(file);
      });
    },

    async onVehiclePhotoChange(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.vehiclePhotoFile = file;
      const reader = new FileReader();
      reader.onload = ev => { this.vehiclePhotoPreview = ev.target.result; };
      reader.readAsDataURL(file);
      
      try {
        const compressed = await this.compressAndRotate(file);
        const fd = new FormData();
        fd.append('file', compressed);
        const res = await axios.post(`${API_BASE}/society/read-plate`, fd, { headers: { 'Content-Type': 'multipart/form-data' }});
        if (res.data) {
          // Handle direct response (detected, plate_text) OR wrapped (status, data)
          const d = res.data.data || res.data;
          const plate = d.plate_text || d.raw_ocr || d.plate || d.plate_number || d.vehicle_number;
          if (plate) {
            this.form.vehicle_no = plate.replace(/\s/g, '').toUpperCase();
          }
        }
      } catch (err) {
        console.error('Plate read error', err);
      }
    },

    async submitCheckin() {
      this.submitError = null;
      // if (!this.photoFile) { this.submitError = 'Please add your photo to continue.'; return; }
      this.submitting = true;
      try {
        const fd = new FormData();
        fd.append('memberId', this.form.member_id);
        if (this.form.unit_mapping_id) {
          fd.append('unit_mapping_id', this.form.unit_mapping_id);
        }
        fd.append('name', this.form.name.trim());
        fd.append('mobileNo', this.form.mobile_no.trim());
        fd.append('country_code', this.form.country_code);
        fd.append('house_number', this.form.house_number);
        if (this.form.flat_id && this.form.flat_id !== 'all_shops') fd.append('flat_id', this.form.flat_id);
        if (this.form.purpose) fd.append('purpose', this.form.purpose);
        if (this.form.vehicle_no) fd.append('vehicleNo', this.form.vehicle_no.trim().toUpperCase());
        fd.append('profilePic', this.photoFile);
        if (this.vehiclePhotoFile) fd.append('vehicleImage', this.vehiclePhotoFile);

        const res = await axios.post(
          `${API_BASE}/society/${this.societyId}/self-checkin`,
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        if (res.data?.status) {
          this.submitted = true;
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          this.submitError = res.data.message || 'Something went wrong. Please try again.';
        }
      } catch {
        this.submitError = 'Unable to submit. Please check your connection and try again.';
      } finally {
        this.submitting = false;
      }
    },
  },

  async mounted() {
    await this.loadSociety();
    this.loadFlats();
  },

  beforeDestroy() {
    document.removeEventListener('click', this.closeUnitDropdown);
    document.removeEventListener('click', this.closeFlatDropdown);
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

* { box-sizing: border-box; margin: 0; padding: 0; }

/* ════════════════════════════════
   PAGE WRAPPER
════════════════════════════════ */
.checkin-page {
  min-height: 100vh;
  font-family: 'Inter', sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px 60px;
  transition: background 0.3s ease;
  position: relative;
}

/* ── Light theme ── */
.theme-light {
  background: linear-gradient(160deg, #f0f4ff 0%, #faf5ff 50%, #f0fdf4 100%);
}
/* ── Dark theme ── */
.theme-dark {
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
}

/* ════════════════════════════════
   THEME TOGGLE BUTTON
════════════════════════════════ */
.theme-toggle {
  position: fixed;
  top: 16px;
  right: 16px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 20px;
  z-index: 9999;
  backdrop-filter: blur(12px);
  transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
}
.theme-light .theme-toggle {
  background: rgba(255,255,255,0.75);
  box-shadow: 0 2px 12px rgba(0,0,0,0.12);
  color: #1e293b !important;   /* dark icon on light bg */
}
.theme-dark .theme-toggle {
  background: rgba(255,255,255,0.1);
  box-shadow: 0 2px 12px rgba(0,0,0,0.4);
  color: #f1f5f9 !important;   /* light icon on dark bg */
}
.theme-toggle:hover { transform: scale(1.1); }

/* ════════════════════════════════
   LOADING / ERROR
════════════════════════════════ */
.loading-screen, .error-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 16px;
}
.loading-spinner {
  width: 48px; height: 48px;
  border: 3px solid rgba(99,102,241,0.2);
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
.loading-text { font-size: 14px; }
.theme-light .loading-text  { color: #64748b; }
.theme-dark  .loading-text  { color: #64748b; }
.error-icon { font-size: 48px; }
.error-screen h2 { font-size: 22px; }
.theme-light .error-screen h2 { color: #1e293b; }
.theme-dark  .error-screen h2 { color: #f1f5f9; }

/* ════════════════════════════════
   SUCCESS SCREEN
════════════════════════════════ */
.success-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  max-width: 460px;
  width: 100%;
  margin-top: 60px;
  text-align: center;
  border-radius: 24px;
  padding: 40px 28px;
  transition: background 0.3s, border-color 0.3s, box-shadow 0.3s;
}
.theme-light .success-screen {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 32px 64px rgba(0,0,0,0.10);
}
.theme-dark .success-screen {
  background: rgba(255,255,255,0.04);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255,255,255,0.08);
  box-shadow: 0 32px 64px rgba(0,0,0,0.4);
}

.success-icon { margin-bottom: 8px; }
.checkmark { width: 80px; height: 80px; }
.checkmark-circle {
  stroke: #10b981; stroke-width: 2;
  stroke-dasharray: 166; stroke-dashoffset: 166;
  animation: stroke 0.6s cubic-bezier(0.65,0,0.45,1) forwards;
}
.checkmark-check {
  stroke: #10b981; stroke-width: 2;
  stroke-linecap: round; stroke-linejoin: round;
  stroke-dasharray: 48; stroke-dashoffset: 48;
  animation: stroke 0.3s cubic-bezier(0.65,0,0.45,1) 0.5s forwards;
}
@keyframes stroke { 100% { stroke-dashoffset: 0; } }

.success-title { font-size: 28px; font-weight: 800; }
.theme-light .success-title { color: #1e293b; }
.theme-dark  .success-title { color: #f1f5f9; }

.success-msg { font-size: 15px; line-height: 1.6; }
.theme-light .success-msg { color: #64748b; }
.theme-dark  .success-msg { color: #94a3b8; }

.success-steps {
  border-radius: 16px;
  padding: 20px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.theme-light .success-steps { background: rgba(0,0,0,0.03); border: 1px solid #e2e8f0; }
.theme-dark  .success-steps { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.08); }

.step { display: flex; align-items: center; gap: 12px; font-size: 14px; }
.theme-light .step { color: #64748b; }
.theme-dark  .step { color: #94a3b8; }

.step-num {
  width: 28px; height: 28px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; color: white;
  flex-shrink: 0;
  background: linear-gradient(135deg, #6366f1, #8b5cf6); /* overridden by inline :style */
}

/* ════════════════════════════════
   MAIN CONTAINER
════════════════════════════════ */
.checkin-container {
  border-radius: 24px;
  width: 100%;
  max-width: 480px;
  overflow: hidden;
  transition: background 0.3s, border-color 0.3s, box-shadow 0.3s;
}
.theme-light .checkin-container {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 32px 64px rgba(0,0,0,0.10);
}
.theme-dark .checkin-container {
  background: rgba(255,255,255,0.04);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255,255,255,0.08);
  box-shadow: 0 32px 64px rgba(0,0,0,0.4);
}

/* ════════════════════════════════
   HEADER  (gradient set via inline :style — always visible)
════════════════════════════════ */
.checkin-header {
  padding: 28px 28px 24px;
  display: flex; align-items: center; gap: 16px;
}
.society-logo-wrap {
  width: 56px; height: 56px;
  border-radius: 14px;
  background: rgba(255,255,255,0.2);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; overflow: hidden;
}
.society-logo { width: 100%; height: 100%; object-fit: cover; }
.society-logo-placeholder { font-size: 20px; font-weight: 800; color: white; letter-spacing: 1px; }
.society-name { font-size: 18px; font-weight: 800; color: white; line-height: 1.2; margin-bottom: 4px; }
.checkin-label { font-size: 12px; color: rgba(255,255,255,0.75); font-weight: 500; text-transform: uppercase; letter-spacing: 0.06em; }

/* ════════════════════════════════
   PROGRESS BAR
════════════════════════════════ */
.progress-wrap {
  padding: 16px 28px 0;
  display: flex; align-items: center; gap: 12px;
}
.progress-bar {
  flex: 1; height: 4px;
  border-radius: 999px; overflow: hidden;
}
.theme-light .progress-bar { background: #e2e8f0; }
.theme-dark  .progress-bar { background: rgba(255,255,255,0.08); }

.progress-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.4s ease;
}
.progress-text { font-size: 11px; white-space: nowrap; }
.theme-light .progress-text { color: #94a3b8; }
.theme-dark  .progress-text { color: #64748b; }

/* ════════════════════════════════
   STEP WRAPPER
════════════════════════════════ */
.form-step {
  padding: 20px 28px 28px;
  display: flex; flex-direction: column; gap: 18px;
}

/* ── Step header ── */
.step-header { text-align: center; margin-bottom: 4px; }
.step-icon {
  display: flex; align-items: center; justify-content: center;
  width: 64px; height: 64px;
  border-radius: 50%;
  margin: 0 auto 16px;
  /* color / bg set via inline :style */
}
.step-title { font-size: 20px; font-weight: 800; margin-bottom: 6px; }
.theme-light .step-title { color: #1e293b; }
.theme-dark  .step-title { color: #f1f5f9; }

.step-subtitle { font-size: 13px; }
.theme-light .step-subtitle { color: #64748b; }
.theme-dark  .step-subtitle { color: #64748b; }

/* ════════════════════════════════
   FORM ELEMENTS
════════════════════════════════ */
.form-group { display: flex; flex-direction: column; gap: 8px; }

.form-label { font-size: 13px; font-weight: 600; }
.theme-light .form-label { color: #475569; }
.theme-dark  .form-label { color: #94a3b8; }

.required { color: #f87171; }
.optional { font-weight: 400; }
.theme-light .optional { color: #94a3b8; }
.theme-dark  .optional { color: #475569; }

.form-input {
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 15px;
  font-family: 'Inter', sans-serif;
  outline: none;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
  width: 100%;
}
.theme-light .form-input {
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  color: #1e293b;
}
.theme-light .form-input::placeholder { color: #cbd5e1; }
.theme-light .form-input:focus {
  border-color: var(--theme-color);
  background: #fff;
  box-shadow: 0 0 0 3px var(--theme-color-ring);
}

.theme-dark .form-input {
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  color: #f1f5f9;
}
.theme-dark .form-input::placeholder { color: #475569; }
.theme-dark .form-input:focus {
  border-color: var(--theme-color);
  background: var(--theme-color-ring);
}

.mt-8 { margin-top: 8px; }

.loading-inline { font-size: 13px; padding: 12px 0; }
.theme-light .loading-inline { color: #94a3b8; }
.theme-dark  .loading-inline { color: #64748b; }

/* ════════════════════════════════
   UNIT SEARCHABLE DROPDOWN
════════════════════════════════ */
.unit-search-wrap { position: relative; }

.unit-search-input-wrap {
  display: flex; align-items: center; gap: 8px;
  border-radius: 12px;
  padding: 12px 40px 12px 16px;
  cursor: pointer;
  min-height: 48px;
  transition: border-color 0.2s;
  position: relative;
}
.theme-light .unit-search-input-wrap {
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
}
.theme-light .unit-search-input-wrap:hover { border-color: var(--theme-color); }
.theme-dark .unit-search-input-wrap {
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
}
.theme-dark .unit-search-input-wrap:hover { border-color: rgba(255,255,255,0.2); }

.unit-search-input-wrap .select-icon {
  position: absolute; right: 14px; top: 50%; transform: translateY(-50%);
  color: #64748b; pointer-events: none; font-size: 12px;
}

.unit-placeholder { font-size: 15px; }
.theme-light .unit-placeholder { color: #94a3b8; }
.theme-dark  .unit-placeholder { color: #475569; }

.unit-selected-num { font-size: 15px; font-weight: 700; }
.theme-light .unit-selected-num { color: #1e293b; }
.theme-dark  .unit-selected-num { color: #f1f5f9; }

.unit-selected-name {
  font-size: 13px; font-weight: 400;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.theme-light .unit-selected-name { color: #64748b; }
.theme-dark  .unit-selected-name { color: #94a3b8; }

/* Dropdown panel */
.unit-dropdown {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 0; right: 0;
  border-radius: 14px;
  z-index: 100;
  overflow: hidden;
}
.theme-light .unit-dropdown {
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  box-shadow: 0 -8px 32px rgba(0,0,0,0.10), 0 -2px 8px rgba(0,0,0,0.06);
}
.theme-dark .unit-dropdown {
  background: #1e293b;
  border: 1px solid rgba(255,255,255,0.12);
  box-shadow: 0 -16px 40px rgba(0,0,0,0.5);
}

.unit-search-box { padding: 10px 12px; }
.theme-light .unit-search-box { border-bottom: 1px solid #f1f5f9; }
.theme-dark  .unit-search-box { border-bottom: 1px solid rgba(255,255,255,0.08); }

.unit-search-field {
  width: 100%;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  font-family: 'Inter', sans-serif;
  outline: none;
}
.theme-light .unit-search-field {
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  color: #1e293b;
}
.theme-light .unit-search-field::placeholder { color: #cbd5e1; }
.theme-dark .unit-search-field {
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  color: #f1f5f9;
}
.theme-dark .unit-search-field::placeholder { color: #475569; }

.unit-list {
  max-height: 200px;
  overflow-y: auto;
  scrollbar-width: thin;
}
.theme-light .unit-list { scrollbar-color: #e2e8f0 transparent; }
.theme-dark  .unit-list { scrollbar-color: rgba(255,255,255,0.1) transparent; }

.unit-option {
  display: flex; align-items: center; gap: 10px;
  padding: 11px 16px;
  cursor: pointer;
  transition: background 0.15s;
}
.theme-light .unit-option:hover    { background: var(--theme-color-soft); }
.theme-light .unit-option.selected { background: var(--theme-color-ring); }
.theme-dark  .unit-option:hover    { background: var(--theme-color-ring); }
.theme-dark  .unit-option.selected { background: var(--theme-color-ring); }

.unit-option-num { font-size: 14px; font-weight: 700; flex-shrink: 0; }
.theme-light .unit-option-num { color: #1e293b; }
.theme-dark  .unit-option-num { color: #f1f5f9; }

.unit-option-name {
  font-size: 12px; font-weight: 400;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.theme-light .unit-option-name { color: #64748b; }
.theme-dark  .unit-option-name { color: #94a3b8; }

.unit-no-result { padding: 16px; text-align: center; font-size: 13px; }
.theme-light .unit-no-result { color: #94a3b8; }
.theme-dark  .unit-no-result { color: #475569; }

/* ════════════════════════════════
   RESIDENT CARD
════════════════════════════════ */
.resident-card {
  display: flex; align-items: center; gap: 14px;
  border-radius: 14px;
  padding: 14px 16px;
  animation: fadeIn 0.3s ease;
}
.theme-light .resident-card { background: #f0fdf4; border: 1.5px solid #bbf7d0; }
.theme-dark  .resident-card { background: rgba(16,185,129,0.08); border: 1px solid rgba(16,185,129,0.25); }

.resident-avatar {
  width: 42px; height: 42px;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; font-weight: 800; color: white; flex-shrink: 0;
}
.resident-info { flex: 1; }
.resident-label { font-size: 11px; display: block; margin-bottom: 2px; text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600; }
.theme-light .resident-label { color: #059669; }
.theme-dark  .resident-label { color: #6ee7b7; }
.resident-name { font-size: 15px; font-weight: 700; }
.theme-light .resident-name { color: #065f46; }
.theme-dark  .resident-name { color: #f1f5f9; }
.resident-check { font-size: 20px; color: #10b981; }

/* ════════════════════════════════
   PURPOSE CHIPS
════════════════════════════════ */
.purpose-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.purpose-chip {
  padding: 7px 14px;
  border-radius: 999px;
  font-size: 12px; font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Inter', sans-serif;
}
.theme-light .purpose-chip {
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  color: #64748b;
}
.theme-light .purpose-chip:hover {
  border-color: var(--theme-color);
  color: var(--theme-color);
  background: var(--theme-color-soft);
}
.theme-dark .purpose-chip {
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.12);
  color: #94a3b8;
}
.theme-dark .purpose-chip:hover {
  border-color: var(--theme-color);
  color: var(--theme-color);
}

.purpose-chip.active {
  background: linear-gradient(135deg, var(--theme-color), var(--theme-color-dark));
  border-color: transparent;
  color: white;
}

/* ════════════════════════════════
   PHONE INPUT
════════════════════════════════ */
.phone-wrap {
  display: flex;
  gap: 8px;
}

/* vue-country-code component ni width vadharo */

.custom-country-code-select {
  width: 100%;
  border: 1px solid var(--border, rgba(0, 0, 0, 0.1)) !important;
  background-color: var(--surface2) !important;
  border-radius: 8px !important;
  transition: all 0.2s ease;
}

/* Theme-specific background/border override */
.theme-light .phone-wrap ::v-deep .custom-country-code-select {
  background-color: #f8fafc !important;
  border: 1.5px solid #e2e8f0 !important;
}
.theme-dark .phone-wrap ::v-deep .custom-country-code-select {
  background-color: rgba(255,255,255,0.06) !important;
  border: 1px solid rgba(255,255,255,0.1) !important;
}
.phone-wrap ::v-deep .vue-country-select {
  flex-shrink: 0;
  width: 90px;   /* pehla ~100px hoy chhe, tamne joiye tema vadharo */
  min-width: 90px;
}

.phone-wrap ::v-deep .dropdown {
  width: 100%;
}

.country-code-select {
  border-radius: 12px;
  padding: 12px 10px;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  outline: none;
  cursor: pointer;
  appearance: none; -webkit-appearance: none;
  flex-shrink: 0; width: 110px;
}
.theme-light .country-code-select {
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  color: #1e293b;
}
.theme-light .country-code-select option { background: #fff; color: #1e293b; }
.theme-dark .country-code-select {
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  color: #f1f5f9;
}
.theme-dark .country-code-select option { background: #1e293b; color: #f1f5f9; }

.phone-input {
  flex: 1;
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 15px;
  font-family: 'Inter', sans-serif;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.theme-light .phone-input {
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  color: #1e293b;
}
.theme-light .phone-input::placeholder { color: #cbd5e1; }
.theme-light .phone-input:focus {
  border-color: var(--theme-color);
  background: #fff;
  box-shadow: 0 0 0 3px var(--theme-color-ring);
}
.theme-dark .phone-input {
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  color: #f1f5f9;
}
.theme-dark .phone-input::placeholder { color: #475569; }
.theme-dark .phone-input:focus {
  border-color: var(--theme-color);
}

/* ════════════════════════════════
   PHOTO UPLOAD
════════════════════════════════ */
.photo-upload-box {
  border-radius: 14px;
  padding: 24px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  min-height: 120px;
  transition: border-color 0.2s, background 0.2s;
  overflow: hidden;
}
.theme-light .photo-upload-box {
  border: 2px dashed var(--theme-color-ring);
}
.theme-light .photo-upload-box:hover {
  border-color: var(--theme-color);
  background: var(--theme-color-soft);
}
.theme-dark .photo-upload-box {
  background: rgba(255,255,255,0.04);
  border: 2px dashed rgba(255,255,255,0.12);
}
.theme-dark .photo-upload-box:hover { border-color: var(--theme-color); }

.photo-placeholder { display: flex; flex-direction: column; align-items: center; gap: 8px; font-size: 13px; font-weight: 500; }
.theme-light .photo-placeholder { color: #94a3b8; }
.theme-dark  .photo-placeholder { color: #64748b; }

.photo-icon { margin-bottom: 4px; }
.theme-light .photo-icon { color: var(--theme-color); }
.theme-dark  .photo-icon { color: var(--theme-color); }

.photo-preview { max-height: 180px; border-radius: 10px; object-fit: cover; }

/* ════════════════════════════════
   SUMMARY CARD
════════════════════════════════ */
.summary-card { border-radius: 14px; padding: 16px; }
.theme-light .summary-card { background: #f8fafc; border: 1.5px solid #e2e8f0; }
.theme-dark  .summary-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); }

.summary-title { font-size: 13px; font-weight: 700; color: var(--theme-color); text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 12px; }
.summary-row { display: flex; justify-content: space-between; align-items: center; padding: 7px 0; }
.theme-light .summary-row { border-bottom: 1px solid #f1f5f9; }
.theme-dark  .summary-row { border-bottom: 1px solid rgba(255,255,255,0.05); }
.summary-row:last-child { border-bottom: none; }

.summary-key { font-size: 12px; font-weight: 500; }
.theme-light .summary-key { color: #94a3b8; }
.theme-dark  .summary-key { color: #64748b; }

.summary-val { font-size: 13px; font-weight: 600; text-align: right; max-width: 65%; }
.theme-light .summary-val { color: #1e293b; }
.theme-dark  .summary-val { color: #f1f5f9; }

/* ════════════════════════════════
   BUTTONS
════════════════════════════════ */
.btn-primary {
  width: 100%;
  padding: 15px;
  border: none;
  border-radius: 14px;
  color: white;
  font-size: 16px; font-weight: 700;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  font-family: 'Inter', sans-serif;
  transition: opacity 0.2s, transform 0.1s;
}
.btn-primary:hover { opacity: 0.9; }
.btn-primary:active { transform: scale(0.98); }
.btn-arrow { font-size: 18px; }

.btn-secondary {
  padding: 14px 20px;
  border-radius: 14px;
  font-size: 15px; font-weight: 600;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s;
  white-space: nowrap;
}
.theme-light .btn-secondary {
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  color: #64748b;
}
.theme-light .btn-secondary:hover {
  border-color: var(--theme-color);
  color: var(--theme-color);
  background: var(--theme-color-soft);
}
.theme-dark .btn-secondary {
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  color: #94a3b8;
}
.theme-dark .btn-secondary:hover {
  border-color: var(--theme-color);
  color: var(--theme-color);
}

.btn-submit {
  flex: 1;
  padding: 15px;
  border: none;
  border-radius: 14px;
  color: white;
  font-size: 16px; font-weight: 700;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  font-family: 'Inter', sans-serif;
  transition: opacity 0.2s;
}
.btn-submit:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-row { display: flex; gap: 10px; align-items: center; }

/* ════════════════════════════════
   ERROR MESSAGE
════════════════════════════════ */
.form-error {
  background: rgba(239,68,68,0.1);
  border: 1px solid rgba(239,68,68,0.3);
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13px; font-weight: 500;
  color: #f87171;
}

/* ════════════════════════════════
   SPINNER
════════════════════════════════ */
.spinner-sm {
  width: 18px; height: 18px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  display: inline-block;
}

/* ════════════════════════════════
   KEYFRAMES
════════════════════════════════ */
@keyframes spin    { to { transform: rotate(360deg); } }
@keyframes fadeIn  { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }

/* ════════════════════════════════
   SLIDE TRANSITION
════════════════════════════════ */
.slide-enter-active, .slide-leave-active { transition: all 0.25s ease; }
.slide-enter  { opacity: 0; transform: translateX(30px); }
.slide-leave-to { opacity: 0; transform: translateX(-30px); }
</style>